import { Link } from "react-router-dom";
import { Play, Lock, Crown, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UnlockButton } from "@/components/UnlockButton";

export const ShowCard = ({ show, index = 0, href }) => {
  const { isSubscribed, isAuthed } = useAuth();
  const gated = !isSubscribed; // ALL content requires an active subscription
  const img = show.thumbnail || show.coverUrl || "/bb-logo.png";

  const unlock = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!isAuthed) window.dispatchEvent(new Event("open-auth"));
    else window.dispatchEvent(new CustomEvent("open-subscribe"));
  };

  const inner = (
    <>
      <div className="aspect-video relative overflow-hidden">
        <img src={img} alt={show.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        {!gated && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="w-12 h-12 rounded-full bg-[#E11D2A] flex items-center justify-center btn-glow"><Play className="w-5 h-5 text-white ml-0.5" /></span>
          </div>
        )}
        {show.isPremium && (
          <span className="absolute top-2.5 right-2.5 flex items-center gap-1 px-2 py-1 rounded-md bg-[#2563EB] text-white text-[10px] font-extrabold uppercase tracking-wide">
            <Crown className="w-3 h-3" /> Premium
          </span>
        )}
        <UnlockButton locked={gated} testid={`unlock-show-${show.id}`} />
        {show.duration && <span className="absolute bottom-2.5 right-2.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-mono">{show.duration}</span>}
        {gated && <div className="absolute top-2.5 left-2.5"><Lock className="w-4 h-4 text-white drop-shadow" /></div>}
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-wider text-[#E11D2A] font-bold">{show.category || "Show"}</div>
          {show.views > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold" data-testid={`show-views-${show.id}`}>
              <Eye className="w-3 h-3" /> {show.views}
            </div>
          )}
        </div>
        <h3 className="text-white font-semibold text-sm mt-1 line-clamp-2 group-hover:text-white/90">{show.title}</h3>
      </div>
    </>
  );

  const cls = "group relative rounded-xl overflow-hidden bg-[#121620] border border-[#273042] card-hover fade-up block text-left w-full";
  const style = { animationDelay: `${Math.min(index * 60, 400)}ms` };

  // Not subscribed → clicking anywhere goes straight to sign-in / payment (no content preview).
  if (gated) {
    return (
      <div role="button" tabIndex={0} onClick={unlock}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); unlock(e); } }}
        aria-label={`${show.title} — sign in or subscribe to watch`}
        data-testid={`show-card-${show.id}`} className={`${cls} cursor-pointer`} style={style}>{inner}</div>
    );
  }
  return <Link to={href || `/watch/${show.id}`} data-testid={`show-card-${show.id}`} className={cls} style={style}>{inner}</Link>;
};
